import {
  type FirebaseOptions,
  getApp,
  getApps,
  initializeApp,
} from "firebase/app";
import { type Auth, connectAuthEmulator, getAuth } from "firebase/auth";
import logger from "@/lib/logger";

logger.info("[Auth] Infrastructure script loaded");

let firebaseConfig: FirebaseOptions = {};
const rawConfig = process.env.NEXT_PUBLIC_FIREBASE_CONFIG || "{}";
const MAX_DECODE_ITERATIONS = 5;

try {
  let decoded = rawConfig.trim();

  // Recursively decode base64 until we find JSON (starts with {) or fail
  // Limit to MAX_DECODE_ITERATIONS iterations to prevent infinite loops
  let iterations = 0;
  while (
    !decoded.startsWith("{") &&
    decoded.length > 0 &&
    iterations < MAX_DECODE_ITERATIONS
  ) {
    try {
      decoded =
        typeof window !== "undefined"
          ? window.atob(decoded)
          : Buffer.from(decoded, "base64").toString("utf-8");
      decoded = decoded.trim();
      iterations++;
    } catch (_e) {
      // If decoding fails, break and try to parse what we have
      break;
    }
  }

  firebaseConfig = decoded !== "" ? JSON.parse(decoded) : {};

  // Force the Auth Domain to the canonical application domain (laffoy.net / verderem.com)
  // This ensures that the Firebase handshake and the session cookie are on the same origin,
  // preventing "Domain Fracture" where getRedirectResult() returns null.
  if (process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN) {
    firebaseConfig.authDomain = process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN;
  }

  // [INSTRUMENTATION] Log the final authDomain to confirm it matches the custom domain
  if (typeof window !== "undefined") {
    logger.info(
      {
        authDomain: firebaseConfig.authDomain,
        projectId: firebaseConfig.projectId,
        envAuthDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
      },
      "[Auth Client] Initialized"
    );
  }
} catch (error) {
  const errorMsg = `❌ [Auth] Failed to parse NEXT_PUBLIC_FIREBASE_CONFIG. Starts with: ${rawConfig.substring(0, 10)}...`;
  logger.error({ err: error }, errorMsg);
  firebaseConfig = {};
}

// Initialize Firebase safely
type VerderemWindow = Window & {
  __VERDEREM_CONFIG__?: { authDisabled?: boolean };
};

const getFirebaseAuth = (): Auth => {
  // Skip Firebase init when auth is disabled.
  // Check runtime config (production builds with injected __VERDEREM_CONFIG__)
  // and also check for missing Firebase config (dev mode where AUTH_DISABLED
  // means no NEXT_PUBLIC_FIREBASE_CONFIG is set).
  if (typeof window !== "undefined") {
    if ((window as VerderemWindow).__VERDEREM_CONFIG__?.authDisabled) {
      return {} as Auth;
    }
    // No Firebase config at all — auth must be disabled or misconfigured.
    // Return stub to avoid crash; useAuthToken handles the disabled path.
    if (!firebaseConfig.apiKey) {
      logger.warn(
        "[Auth] No Firebase config found — returning stub Auth (auth likely disabled)"
      );
      return {} as Auth;
    }
  }

  const requiredKeys: (keyof FirebaseOptions)[] = [
    "apiKey",
    "authDomain",
    "projectId",
  ];
  const missingKeys = requiredKeys.filter((key) => !firebaseConfig[key]);

  // Log critical config values (masked) to help diagnose configuration issues
  if (typeof window !== "undefined") {
    logger.info(
      `[Auth] Initializing Firebase with Project ID: ${
        firebaseConfig.projectId || "MISSING"
      }, Auth Domain: ${firebaseConfig.authDomain || "MISSING"}, API Key: ${
        firebaseConfig.apiKey
          ? `${firebaseConfig.apiKey.substring(0, 5)}...`
          : "MISSING"
      }`
    );

    if (missingKeys.length > 0 && process.env.NODE_ENV !== "development") {
      const errorMsg = `Firebase Configuration is incomplete! Missing keys: ${missingKeys.join(
        ", "
      )}. This usually means NEXT_PUBLIC_FIREBASE_CONFIG was not provided correctly at build time.`;
      logger.error(`❌ [Auth Error] ${errorMsg}`);
      throw new Error(errorMsg);
    }
  }

  // During build time/SSG on server, if keys are missing, return a dummy to allow the build to pass.
  if (missingKeys.length > 0 && typeof window === "undefined") {
    return {} as Auth;
  }

  const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
  const auth = getAuth(app);

  // Validation: Ensure the auth object is actually functional
  if (
    typeof window !== "undefined" &&
    typeof auth.onAuthStateChanged !== "function"
  ) {
    throw new Error(
      "❌ [Auth] Initialized auth object is invalid. Check your configuration JSON keys."
    );
  }

  if (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_FIREBASE_EMULATOR_HOST
  ) {
    const isEmulator = (auth as any).emulatorConfig !== null;
    if (!isEmulator) {
      connectAuthEmulator(
        auth,
        process.env.NEXT_PUBLIC_FIREBASE_EMULATOR_HOST,
        {
          disableWarnings: true,
        }
      );
    }
  }

  return auth;
};

export const auth =
  typeof window !== "undefined" ? getFirebaseAuth() : ({} as Auth);

// DEV ONLY — expose window.devSignIn() for local development without OAuth.
// Fetches a custom token from the server, signs in with it, then creates a
// session cookie so the app behaves exactly as if you logged in via Google.
if (process.env.NODE_ENV === "development" && typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).devSignIn = async () => {
    try {
      const { signInWithCustomToken } = await import("firebase/auth");
      logger.info("[DevAuth] Fetching dev token...");
      const res = await fetch("/api/auth/dev-token");
      if (!res.ok) {
        logger.error(
          { status: res.status, body: await res.text() },
          "[DevAuth] Failed to fetch dev token"
        );
        return;
      }
      const { customToken } = (await res.json()) as { customToken: string };
      const userCred = await signInWithCustomToken(auth, customToken);
      const idToken = await userCred.user.getIdToken();
      const loginRes = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (!loginRes.ok) {
        logger.error(
          { status: loginRes.status, body: await loginRes.text() },
          "[DevAuth] Failed to create session cookie"
        );
        return;
      }
      logger.info("[DevAuth] Signed in — reloading...");
      window.location.reload();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      logger.error(
        { err: error, message },
        "[DevAuth] Unexpected error during dev sign-in"
      );
    }
  };
  logger.info("[DevAuth] Run window.devSignIn() in the console to sign in");
}

/**
 * Signs out the user from both the Firebase client and the server-side session.
 */
export async function signOut() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    // 1. Sign out from Firebase client
    const firebaseAuth = getFirebaseAuth();
    await firebaseAuth.signOut();

    // 2. Clear the server-side session cookie
    await fetch("/api/auth/logout", { method: "POST" });

    // 3. Redirect to login with the `signedout` flag so the login page does
    //    NOT auto-re-authenticate a restored/persisted Firebase session — that
    //    would immediately log the same account back in and defeat the logout.
    window.location.href = "/login?signedout=1";
  } catch (error) {
    logger.error({ err: error }, "Logout failed");
    // Fallback redirect — keep the flag so auto-login is still suppressed.
    window.location.href = "/login?signedout=1";
  }
}
